import { useCallback } from "react";
import { useParams } from "react-router";
import { createApiResponse } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import useIsNewsVisibleToInvestors from "../useIsNewsVisibleToInvestors";
import InvestorCommunicationDetails from "./InvestorCommunicationDetails";

const InvestorCommunicationDetailsPage = () => {
  usePageTitle("Investor Communication Details");

  const { id = "" } = useParams();
  const { hasPermissions, accessCategories } = useClientContext();

  const hasEditPermissions = hasPermissions(["ManageInvestorCommunications"]);

  const getInvestorCommunication = useCallback(() => adminApi.getInvestorCommunicationById(id), [id]);

  const getRecipients = useCallback(() => adminApi.getInvestorCommunicationRecipients(id), [id]);

  const getFunds = useCallback(() => adminApi.getObjectsAsLookupReferences("Fund"), []);

  const [
    investorCommunication,
    fetchCommunicationError,
    { isFetching: isFetchingCommunication, setData: setInvestorCommunication },
  ] = useFetch(getInvestorCommunication);

  const [recipients, fetchRecipientsError, { fetch: refreshRecipients }] = useFetch(getRecipients);

  const [funds, fetchFundsError] = useFetch(getFunds, { disabled: !hasEditPermissions });

  const status = investorCommunication?.status;
  const isLive = status === "Live";
  const canHaveEngagement = isLive || status === "Preview";
  const getEngagement = useCallback(
    async () => (canHaveEngagement ? adminApi.getInvestorCommunicationEngagement(id) : createApiResponse(undefined)),
    [id, canHaveEngagement]
  );
  const [engagement, fetchEngagementError, { fetch: refreshEngagement }] = useFetch(getEngagement, {
    disabled: investorCommunication === undefined,
  });

  const refreshAudience = useCallback(async () => {
    await Promise.all([refreshRecipients(), refreshEngagement()]);
  }, [refreshRecipients, refreshEngagement]);

  const isNewsVisibleToInvestors = useIsNewsVisibleToInvestors();

  const fetchError =
    fetchCommunicationError || fetchRecipientsError || fetchFundsError || (isLive ? fetchEngagementError : undefined);

  if (fetchError) {
    logError(fetchError, "[InvestorCommunicationDetailsPage] fetch");
    return <DataLoadingFailed title="Failed to load investor communication details" />;
  }

  if (fetchEngagementError) {
    logError(fetchEngagementError, "[InvestorCommunicationDetailsPage] getEngagement");
  }

  const isFundsPending = hasEditPermissions && funds === undefined;
  const isEngagementPending = isLive && engagement === undefined;
  const hasReaders = engagement !== undefined && engagement.readers.length > 0;

  if (
    isFetchingCommunication ||
    investorCommunication === undefined ||
    recipients === undefined ||
    isFundsPending ||
    isEngagementPending
  ) {
    return <InlineLoader />;
  }

  return (
    <InvestorCommunicationDetails
      key={investorCommunication.id}
      investorCommunication={investorCommunication}
      recipients={recipients}
      engagement={isLive || hasReaders ? engagement : undefined}
      funds={funds ?? []}
      categories={accessCategories.filter((c) => c.type === "Message")}
      hasEditPermissions={hasEditPermissions}
      isNewsVisibleToInvestors={isNewsVisibleToInvestors}
      onSaved={setInvestorCommunication}
      onRecipientsRefresh={refreshAudience}
    />
  );
};

export default InvestorCommunicationDetailsPage;
